/**
 * PH-A1.3 follow-up — consistent display capitalization for the
 * `siteResearch.waterRights.doctrine` field.
 *
 * The `siteResearchProvider` returns the doctrine as a free-form
 * STRING (no enum in the Gemini schema), so the same site can show up
 * as "Riparian", "RIPARIAN", "riparian", "prior appropriation",
 * "Prior Appropriation", etc. across runs. The case-insensitive
 * LOOKUP at `siteRagService.ts` (`lookupWaterRightsScore`) already
 * normalizes for scoring, but every USER-FACING string interpolation
 * embeds the raw value — so the same site renders differently in the
 * UI, the report appendix, the DOCX export, and the SED summary
 * depending on which casing the model returned this run.
 *
 * This helper normalizes ONLY for display:
 *   - Known canonical doctrines are mapped to canonical title-cased
 *     labels (so "PRIOR APPROPRIATION" and "prior appropriation"
 *     both render as "Prior Appropriation").
 *   - Unknown doctrines fall through to a generic title-case
 *     transformation, preserving the AI's words verbatim but with
 *     consistent capitalization.
 *   - Null / undefined / empty / whitespace-only input returns
 *     `'Unknown'` so call sites don't need to repeat the guard.
 *
 * NOT used for lookups — keep `lookupWaterRightsScore` as the
 * scoring-side normalizer. Display normalization is intentionally
 * lossy (preserves canonical label spelling) where the scorer is
 * strict (preserves the lookup contract).
 */

const CANONICAL_DOCTRINE_LABELS: Record<string, string> = {
  'riparian': 'Riparian',
  'prior appropriation': 'Prior Appropriation',
  'hybrid': 'Hybrid',
};

/**
 * Title-case a single token. Multi-character all-caps tokens are
 * treated as acronyms and preserved (e.g. "USA"), matching the
 * convention in `lib/docFileName.ts`. Pure digit runs pass through.
 */
function titleCaseToken(tok: string): string {
  if (!tok) return tok;
  if (tok.length > 1 && tok === tok.toUpperCase() && /[A-Z]/.test(tok)) {
    // Heuristic: an all-caps token like "RIPARIAN" coming from a
    // model that shouted the whole field is NOT an acronym — fall
    // through to the standard title-case path below.
    // (True acronyms like "USA" are typically embedded in a longer
    // string and surrounded by lowercased tokens; they're handled by
    // the canonical-label path above when relevant.)
    return tok.charAt(0).toUpperCase() + tok.slice(1).toLowerCase();
  }
  if (/^\d+$/.test(tok)) return tok;
  return tok.charAt(0).toUpperCase() + tok.slice(1).toLowerCase();
}

/**
 * Format a water-rights doctrine for user-facing display.
 *
 * Accepts the raw AI-returned string (any casing) and returns a
 * consistently-cased label. Returns `'Unknown'` for missing input
 * so call sites can interpolate the result directly.
 */
export function formatDoctrineDisplay(
  doctrine: string | null | undefined,
): string {
  if (typeof doctrine !== 'string') return 'Unknown';
  const trimmed = doctrine.trim();
  if (trimmed.length === 0) return 'Unknown';

  const key = trimmed.toLowerCase();
  const canonical = CANONICAL_DOCTRINE_LABELS[key];
  if (canonical) return canonical;

  // Fall through: title-case the raw string token-by-token.
  return trimmed.split(/\s+/).map(titleCaseToken).join(' ');
}
